import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getAllBalanceTransactions,
  getAllFawryTransactions,
  getFawryTournaments,
  getAllInstaPayRegistrations,
  confirmInstaPayRegistration,
  rejectInstaPayRegistration,
  confirmInstaPayReservation,
  getInstaPayTournaments,
  InstaPayTournamentOptionDTO,
} from "../../api/payments";
import { BalanceTransactionDTO } from "../../models/IBalance";
import { FawryTransactionDTO, InstaPayRegistrationDTO } from "../../models/IFawryTransaction";
import { getPendingTopUps, reviewTopUp } from "../../api/balance";
import { getAllClashPointsTransactions } from "../../api/points";
import { AdminClashPointsTransactionDTO } from "../../models/IClashPoints";
import { DataTable } from "../../components";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useDebouncedSearch } from "../../hooks";
import { Image as ImageIcon, Check, X, Download, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import moment from "moment";
import {
  Button,
  ConfirmDialog,
  DatePicker,
  DescriptionList,
  PageHeader,
  Segmented,
  Select,
  Tabs,
  Tag,
  Toolbar,
} from "../../ui";
import type { DescriptionItem, TagTone } from "../../ui";
import "./styles.scss";

type Tab = "instapay" | "all" | "payments" | "clash-points";

// Keeps the last non-null value, so a dialog still has its subject while it plays its closing animation.
function useLastDefined<T>(value: T | null): T | null {
  const last = useRef<T | null>(value);
  if (value !== null) last.current = value;
  return last.current;
}

// All UTC timestamps from the backend are surfaced in Cairo time with a 12-hour
// AM/PM clock. Intl handles Egypt's DST transitions for us.
const cairoDateTimeFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
  timeZone: "Africa/Cairo",
});
const cairoDateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: "Africa/Cairo",
});
const cairoTimeFmt = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
  timeZone: "Africa/Cairo",
});
const fmtCairoDateTime = (v: string | Date | null | undefined) =>
  v ? cairoDateTimeFmt.format(new Date(v)) : "-";
const fmtCairoDate = (v: string | Date | null | undefined) =>
  v ? cairoDateFmt.format(new Date(v)) : "-";
const fmtCairoTime = (v: string | Date | null | undefined) =>
  v ? cairoTimeFmt.format(new Date(v)) : "-";

// Compares two timestamps for "same calendar day" *in Cairo* — used when
// formatting tournament/reservation date ranges so a single-day window collapses
// to one date instead of "27 Apr – 27 Apr".
const isSameCairoDay = (a: string | Date, b: string | Date) =>
  cairoDateFmt.format(new Date(a)) === cairoDateFmt.format(new Date(b));

const formatEGP = (n: number) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EGP";

// Money in a known currency. Used by the InstaPay review queue, where a row can
// be RUB/EUR/… — the wallet (balance) and Fawry views stay on formatEGP because
// both are genuinely EGP-only.
const formatMoney = (n: number, currency?: string | null) =>
  Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
  " " + ((currency ?? "").trim() || "EGP");

const TotalAmountPill = ({ amount, count, label, currency }: { amount: number | null | undefined; count: number; label: string; currency?: string | null }) => {
  // The API sends null for TotalAmount when the filtered set mixes currencies —
  // summing across them is meaningless, so the pill is suppressed entirely.
  if (amount === undefined || amount === null) return null;
  return (
    <div className="txn-total-amount">
      <span className="txn-total-amount-label">{count.toLocaleString()} {label}{count === 1 ? "" : "s"} · Total</span>
      <span className="txn-total-amount-value">{formatMoney(amount, currency)}</span>
    </div>
  );
};

// ── CSV export ──
// Escape a single cell for CSV: wrap in quotes if it contains comma/quote/newline
// and double any embedded quotes per RFC 4180.
const csvCell = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

type CsvColumn<T> = { header: string; value: (row: T) => unknown };

const downloadCsv = <T,>(rows: T[], columns: CsvColumn<T>[], filename: string) => {
  const lines = [
    columns.map((c) => csvCell(c.header)).join(","),
    ...rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(",")),
  ];
  // BOM lets Excel pick up UTF-8 cleanly (Arabic team/court names render correctly).
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ExportButton = ({
  onExport,
  disabled,
}: {
  onExport: () => Promise<void> | void;
  disabled?: boolean;
}) => {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="tint"
      icon={<Download size={14} />}
      loading={busy}
      disabled={disabled}
      onClick={async () => {
        setBusy(true);
        try {
          await onExport();
        } finally {
          setBusy(false);
        }
      }}
      aria-label="Export transactions"
    >
      {busy ? "Exporting..." : "Export"}
    </Button>
  );
};

const todayStamp = () => {
  // Cairo-local YYYY-MM-DD for filenames so the export filename matches the
  // dashboard's displayed dates.
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit",
    timeZone: "Africa/Cairo",
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
};

const STATUS_TONES: Record<string, TagTone> = {
  Approved: "ok", Paid: "ok",
  Pending: "warn", Initiated: "warn",
  Rejected: "danger", Failed: "danger",
  Expired: "neutral", Cancelled: "neutral",
  Refunded: "info",
};

const StatusBadge = ({ status }: { status: string }) => (
  <Tag tone={STATUS_TONES[status] || "neutral"}>{status}</Tag>
);

// A ledger entry's type, green when it credits the account and red when it debits it.
const LedgerTypeTag = ({ amount, children }: { amount: unknown; children: React.ReactNode }) => (
  <Tag tone={Number(amount) > 0 ? "ok" : "danger"}>{children}</Tag>
);

// The tournament-date filter: the kit date picker (brand calendar, clearable).
const DateFilterInput = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div className="txn-date-filter">
    <DatePicker
      selected={value ? moment(value).toDate() : null}
      onChange={(date) => onChange(date ? moment(date).format("YYYY-MM-DD") : "")}
      dateFormat="dd MMM yyyy"
      placeholderText="Filter by date"
      aria-label="Filter by date"
      isClearable
    />
  </div>
);

// A labelled group in a filter toolbar ("Status", "Purpose", "Tournament"…).
const FilterGroup = ({ label, className, children }: { label: string; className?: string; children: React.ReactNode }) => (
  <div className={["txn-filter", className].filter(Boolean).join(" ")} role="group" aria-label={label}>
    <span className="txn-filter__label">{label}</span>
    <div className="txn-filter__controls">{children}</div>
  </div>
);

// ── Image lightbox ──
// Zoomable receipt viewer. Wheel/+/-/buttons zoom; click toggles 1× ↔ 2×;
// drag-to-pan when zoomed. Keyboard: Esc closes, +/- zoom, 0 resets.
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;
const WHEEL_STEP = 0.25;

const ImageLightbox = ({ src, onClose }: { src: string; onClose: () => void }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; startX: number; startY: number } | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Reset zoom + pan when the lightbox is opened on a different image.
  useEffect(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, [src]);

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  const setScaleClamped = useCallback((next: number) => {
    setScale((prev) => {
      const n = Math.min(MAX_SCALE, Math.max(MIN_SCALE, next));
      // Snapping back to 1 also resets the pan so the image re-centers cleanly.
      if (n <= MIN_SCALE) setPos({ x: 0, y: 0 });
      return n;
    });
  }, []);

  const zoomIn = useCallback(() => setScaleClamped(scale + SCALE_STEP), [scale, setScaleClamped]);
  const zoomOut = useCallback(() => setScaleClamped(scale - SCALE_STEP), [scale, setScaleClamped]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomIn(); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomOut(); }
      else if (e.key === "0") { e.preventDefault(); reset(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, zoomIn, zoomOut, reset]);

  // Wheel zoom needs a non-passive listener so we can preventDefault — React's
  // synthetic onWheel binds passive in modern browsers and warns otherwise.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP;
      setScaleClamped(scale + delta);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [scale, setScaleClamped]);

  const onImgClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    // Skip toggle when the user just finished a pan drag.
    if (dragStartRef.current) {
      const moved = Math.hypot(
        e.clientX - dragStartRef.current.mouseX,
        e.clientY - dragStartRef.current.mouseY,
      );
      if (moved > 4) return;
    }
    if (scale === MIN_SCALE) setScaleClamped(2);
    else reset();
  };

  const onImgMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (scale <= MIN_SCALE) return;
    e.preventDefault();
    e.stopPropagation();
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startX: pos.x,
      startY: pos.y,
    };
    setDragging(true);
  };

  // Bind drag-move/up to the window so panning continues even if the cursor
  // strays off the image while zoomed in.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      setPos({
        x: dragStartRef.current.startX + (e.clientX - dragStartRef.current.mouseX),
        y: dragStartRef.current.startY + (e.clientY - dragStartRef.current.mouseY),
      });
    };
    const onUp = () => {
      setDragging(false);
      // Keep dragStartRef briefly so the click handler can detect "this was a drag".
      window.setTimeout(() => { dragStartRef.current = null; }, 0);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging]);

  // ── Touch: single-finger pan when zoomed, two-finger pinch to zoom. ──
  const pinchRef = useRef<{ startDist: number; startScale: number } | null>(null);
  const touchPanRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);

  const onTouchStart = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { startDist: Math.hypot(dx, dy), startScale: scale };
    } else if (e.touches.length === 1 && scale > MIN_SCALE) {
      touchPanRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        baseX: pos.x,
        baseY: pos.y,
      };
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length === 2 && pinchRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / pinchRef.current.startDist;
      setScaleClamped(pinchRef.current.startScale * ratio);
    } else if (e.touches.length === 1 && touchPanRef.current && scale > MIN_SCALE) {
      e.preventDefault();
      setPos({
        x: touchPanRef.current.baseX + (e.touches[0].clientX - touchPanRef.current.startX),
        y: touchPanRef.current.baseY + (e.touches[0].clientY - touchPanRef.current.startY),
      });
    }
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLImageElement>) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) touchPanRef.current = null;
  };

  return (
    <div
      className="txn-lightbox"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      ref={containerRef}
    >
      <button className="txn-lightbox-close" onClick={onClose} aria-label="Close">
        <X size={22} />
      </button>

      <div
        className="txn-lightbox-toolbar"
        onClick={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="txn-lightbox-tool-btn"
          onClick={zoomOut}
          disabled={scale <= MIN_SCALE}
          aria-label="Zoom out"
          data-tooltip="Zoom out (-)"
        >
          <ZoomOut size={16} />
        </button>
        <span className="txn-lightbox-tool-zoom">{Math.round(scale * 100)}%</span>
        <button
          type="button"
          className="txn-lightbox-tool-btn"
          onClick={zoomIn}
          disabled={scale >= MAX_SCALE}
          aria-label="Zoom in"
          data-tooltip="Zoom in (+)"
        >
          <ZoomIn size={16} />
        </button>
        <button
          type="button"
          className="txn-lightbox-tool-btn"
          onClick={reset}
          disabled={scale === MIN_SCALE && pos.x === 0 && pos.y === 0}
          aria-label="Reset zoom"
          data-tooltip="Reset (0)"
        >
          <RotateCcw size={16} />
        </button>
      </div>

      <img
        src={src}
        alt="Payment receipt"
        draggable={false}
        onClick={onImgClick}
        onMouseDown={onImgMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transformOrigin: "center center",
          cursor: scale > MIN_SCALE ? (dragging ? "grabbing" : "grab") : "zoom-in",
          transition: dragging ? "none" : "transform 0.15s ease-out",
          touchAction: "none",
          userSelect: "none",
        }}
      />

      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        className="txn-lightbox-open"
        onClick={(e) => e.stopPropagation()}
      >
        Open original
      </a>
    </div>
  );
};

// The label/value summary under a confirm dialog's message.
const DialogMeta = ({ items }: { items: (DescriptionItem | false | null | undefined | "")[] }) => (
  <DescriptionList
    className="txn-dialog-meta"
    columns={1}
    items={items.filter(Boolean) as DescriptionItem[]}
  />
);

// ── Confirm dialog ──
// The kit ConfirmDialog closes itself with `confirmed: true` after onConfirm resolves; the handlers here catch their
// own errors and clear the row only on success, so only a cancel (confirmed: false) is passed on — on a failure the
// dialog stays open, as before.
const ConfirmPaymentDialog = ({
  open, row, busy, onCancel, onConfirm,
}: {
  open: boolean;
  row: InstaPayRegistrationDTO;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) => {
  const isReservation = row.type === "Reservation";
  const isTeamPayment = !isReservation && row.payForTeam && row.teamMemberCount > 1;
  const subjectName = row.playerName || row.guestName || row.teamName || row.courtName || "this payment";
  return (
    <ConfirmDialog
      open={open}
      onClose={(confirmed) => { if (!confirmed) onCancel(); }}
      onConfirm={onConfirm}
      loading={busy}
      tone="primary"
      title="Confirm this payment?"
      confirmLabel={busy ? "Confirming..." : "Confirm payment"}
      message={
        isTeamPayment ? (
          <>
            This will mark <strong>{subjectName}</strong> and all <strong>{row.teamMemberCount} teammates</strong> on
            <strong> {row.teamName}</strong> as <strong>Paid</strong> and <strong>Confirmed</strong>. This cannot be undone from this screen.
          </>
        ) : (
          <>
            This will mark <strong>{subjectName}</strong> as <strong>Paid</strong> and
            set the {isReservation ? "reservation" : "registration"} status to <strong>Confirmed</strong>. This cannot be undone from this screen.
          </>
        )
      }
    >
      <DialogMeta
        items={[
          ...(isReservation
            ? [
                { label: "Court", value: row.courtName || "-" },
                !!row.reservationStartTime && {
                  label: "Time",
                  value: (
                    <>
                      {fmtCairoDateTime(row.reservationStartTime)}
                      {row.reservationEndTime ? ` – ${fmtCairoTime(row.reservationEndTime)}` : ""}
                    </>
                  ),
                },
              ]
            : [
                { label: "Team", value: <>{row.teamName}{isTeamPayment ? ` · ${row.teamMemberCount} players` : ""}</> },
                !!row.tournamentName && {
                  label: "Tournament",
                  value: <>{row.tournamentName}{row.categoryName ? ` · ${row.categoryName}` : ""}</>,
                },
              ]),
          {
            label: "Amount",
            value: (
              <>
                {formatMoney(row.amount, row.currency)}
                {isTeamPayment && <span className="txn-cell-muted txn-meta-note">· whole-team payment</span>}
              </>
            ),
          },
        ]}
      />
    </ConfirmDialog>
  );
};

// ── Reject dialog ──
const RejectPaymentDialog = ({
  open, row, busy, onCancel, onReject,
}: {
  open: boolean;
  row: InstaPayRegistrationDTO;
  busy: boolean;
  onCancel: () => void;
  onReject: () => Promise<void> | void;
}) => {
  const subjectName = row.playerName || row.teamName || "this payment";
  return (
    <ConfirmDialog
      open={open}
      onClose={(confirmed) => { if (!confirmed) onCancel(); }}
      onConfirm={onReject}
      loading={busy}
      tone="danger"
      title="Reject this payment?"
      confirmLabel={busy ? "Rejecting..." : "Reject payment"}
      message={
        <>
          This will mark <strong>{subjectName}</strong>'s payment as <strong>Rejected</strong>.
          The registration status is left unchanged so the player can submit a new receipt for review.
        </>
      }
    >
      <DialogMeta
        items={[
          { label: "Team", value: <>{row.teamName}</> },
          !!row.tournamentName && {
            label: "Tournament",
            value: <>{row.tournamentName}{row.categoryName ? ` · ${row.categoryName}` : ""}</>,
          },
          { label: "Amount", value: formatMoney(row.amount, row.currency) },
        ]}
      />
    </ConfirmDialog>
  );
};

// ── Top-up review dialog (approve or reject a pending balance top-up) ──
const TopUpReviewDialog = ({
  open, action, busy, onCancel, onConfirm,
}: {
  open: boolean;
  action: { row: BalanceTransactionDTO; approve: boolean };
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
}) => (
  <ConfirmDialog
    open={open}
    onClose={(confirmed) => { if (!confirmed) onCancel(); }}
    onConfirm={onConfirm}
    loading={busy}
    tone={action.approve ? "primary" : "danger"}
    title={action.approve ? "Approve this top-up?" : "Reject this top-up?"}
    confirmLabel={busy ? "Saving..." : action.approve ? "Approve top-up" : "Reject top-up"}
    message={
      action.approve ? (
        <>
          This will credit <strong>{action.row.userName}</strong>'s balance with
          <strong> {Number(action.row.amount).toFixed(2)} EGP</strong> plus the standard top-up bonus.
        </>
      ) : (
        <>
          This will reject the top-up request from <strong>{action.row.userName}</strong>.
          The user's balance will not be changed.
        </>
      )
    }
  >
    <DialogMeta
      items={[
        { label: "Player", value: <>{action.row.userName}</> },
        { label: "Amount", value: `${Number(action.row.amount).toFixed(2)} EGP` },
        { label: "Submitted", value: fmtCairoDateTime(action.row.createdAt) },
      ]}
    />
  </ConfirmDialog>
);

// ── InstaPay Registrations ──
type PaymentStatusOption = "" | "Pending" | "Paid" | "Rejected";

const InstaPayRegistrationsTab = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  // Default to Pending — that's what admins most often need to action.
  const paymentStatusFilter = (searchParams.get("paymentStatus") as PaymentStatusOption | null) ?? "Pending";
  const tournamentDateFilter = searchParams.get("tournamentDate") || "";
  const tournamentIdFilter = searchParams.get("tournamentId") || "";
  const purposeFilter = searchParams.get("purpose") || "";
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [pendingRow, setPendingRow] = useState<InstaPayRegistrationDTO | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [pendingRejectRow, setPendingRejectRow] = useState<InstaPayRegistrationDTO | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dateTournaments, setDateTournaments] = useState<InstaPayTournamentOptionDTO[]>([]);
  const [dateTournamentsLoading, setDateTournamentsLoading] = useState(false);

  // Top-up requests: rendered alongside registrations when no purpose filter
  // is set, or on their own when the Purpose filter is "TopUp".
  const isTopUpView = purposeFilter === "TopUp";
  const showTopUps = purposeFilter === "" || isTopUpView;
  const showRegistrations = !isTopUpView;
  const [topUps, setTopUps] = useState<BalanceTransactionDTO[]>([]);
  const [topUpsLoading, setTopUpsLoading] = useState(false);
  const [topUpAction, setTopUpAction] = useState<{ row: BalanceTransactionDTO; approve: boolean } | null>(null);
  const [topUpBusyId, setTopUpBusyId] = useState<string | null>(null);
  // What the confirm dialogs show while they close
  const shownPendingRow = useLastDefined(pendingRow);
  const shownRejectRow = useLastDefined(pendingRejectRow);
  const shownTopUpAction = useLastDefined(topUpAction);

  const fetchTopUps = useCallback(async () => {
    setTopUpsLoading(true);
    try {
      const res = await getPendingTopUps();
      const items = (res?.data as any)?.data ?? res?.data ?? [];
      setTopUps(Array.isArray(items) ? items : []);
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to load pending top-ups" }));
    } finally {
      setTopUpsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (showTopUps) fetchTopUps();
  }, [showTopUps, fetchTopUps]);

  const handleReviewTopUp = async () => {
    if (!topUpAction) return;
    const { row, approve } = topUpAction;
    setTopUpBusyId(row.id);
    try {
      await reviewTopUp({ transactionId: row.id, approve });
      dispatch(setGeneralAlertMessage({
        type: "success",
        message: approve ? "Top-up approved" : "Top-up rejected",
      }));
      setTopUpAction(null);
      await fetchTopUps();
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to review top-up" }));
    } finally {
      setTopUpBusyId(null);
    }
  };

  // Fetch tournaments that have InstaPay registrations and overlap the
  // selected date. Backend-driven so we don't rely on the published-only
  // tournament list, and we only surface tournaments that are actually
  // actionable from this page.
  useEffect(() => {
    if (!tournamentDateFilter) {
      setDateTournaments([]);
      return;
    }
    setDateTournamentsLoading(true);
    getInstaPayTournaments(tournamentDateFilter)
      .then(({ data }) => setDateTournaments(data || []))
      .catch(() => setDateTournaments([]))
      .finally(() => setDateTournamentsLoading(false));
  }, [tournamentDateFilter]);

  // If the currently-selected tournament isn't in the list for the chosen date,
  // clear it so the chained filter stays consistent.
  useEffect(() => {
    if (!tournamentIdFilter || !tournamentDateFilter) return;
    if (dateTournamentsLoading) return;
    if (!dateTournaments.some((t) => t.id === tournamentIdFilter)) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete("tournamentId");
        p.delete("page");
        return p;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateTournaments, dateTournamentsLoading]);

  const fetchRef = useRef((page: number, size: number, search: string, sort: string, dir: string) =>
    getAllInstaPayRegistrations(page, size, search, paymentStatusFilter, tournamentIdFilter || undefined, tournamentDateFilter || undefined, sort, dir, purposeFilter || undefined));
  fetchRef.current = (page, size, search, sort, dir) =>
    getAllInstaPayRegistrations(page, size, search, paymentStatusFilter, tournamentIdFilter || undefined, tournamentDateFilter || undefined, sort, dir, purposeFilter || undefined);

  const t = usePaginatedTab<InstaPayRegistrationDTO>(fetchRef, searchParams, setSearchParams);

  const rowKey = (row: InstaPayRegistrationDTO) =>
    (row.type as string) === "TopUp"
      ? row.teamMemberId // we stash the transaction id here for top-up rows
      : row.type === "Reservation"
      ? (row.reservationId ?? "")
      : row.teamMemberId;

  const isTopUpRow = (row: InstaPayRegistrationDTO) => (row.type as string) === "TopUp";

  // Map a top-up DTO into the registration row shape so it can render in the
  // same DataTable. Irrelevant columns get null/"-" defaults.
  const topUpToRow = (tx: BalanceTransactionDTO): InstaPayRegistrationDTO => ({
    type: "TopUp" as any,
    currency: "EGP", // balance top-ups are always EGP
    teamMemberId: tx.id,
    teamId: "",
    teamName: "",
    playerId: tx.userId,
    playerName: tx.userName,
    tournamentId: null,
    tournamentName: null,
    tournamentStartDate: null,
    tournamentEndDate: null,
    reservationId: null,
    courtId: null,
    courtName: null,
    reservationStartTime: null,
    reservationEndTime: null,
    guestName: null,
    guestEmail: null,
    guestPhone: null,
    categoryName: null,
    amount: tx.amount,
    payForTeam: false,
    teamMemberCount: 1,
    receiptScreenshot: tx.attachment ?? null,
    paymentStatus: "Pending",
    registrationStatus: "-",
    createdAt: tx.createdAt,
  });

  const handleConfirm = async () => {
    if (!pendingRow) return;
    const id = rowKey(pendingRow);
    if (!id) return;
    setConfirmingId(id);
    try {
      if (pendingRow.type === "Reservation") {
        await confirmInstaPayReservation(id);
      } else {
        await confirmInstaPayRegistration(id);
      }
      dispatch(setGeneralAlertMessage({ type: "success", message: "Payment confirmed" }));
      setPendingRow(null);
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set("_r", String(Date.now()));
        return p;
      });
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to confirm payment" }));
    } finally {
      setConfirmingId(null);
    }
  };

  const handleReject = async () => {
    if (!pendingRejectRow) return;
    const id = rowKey(pendingRejectRow);
    if (!id) return;
    setRejectingId(id);
    try {
      await rejectInstaPayRegistration(id);
      dispatch(setGeneralAlertMessage({ type: "success", message: "Payment rejected" }));
      setPendingRejectRow(null);
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set("_r", String(Date.now()));
        return p;
      });
    } catch (err: any) {
      dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || "Failed to reject payment" }));
    } finally {
      setRejectingId(null);
    }
  };

  const handlePaymentStatusChange = (value: PaymentStatusOption) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      // "Pending" is the default; keep the URL clean by omitting it
      if (value && value !== "Pending") p.set("paymentStatus", value);
      else if (value === "Pending") p.delete("paymentStatus");
      else p.set("paymentStatus", ""); // All — explicit empty to distinguish from default
      p.delete("page");
      return p;
    });
  };

  const handleTournamentDateChange = (value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set("tournamentDate", value);
      else p.delete("tournamentDate");
      // Clearing the date also clears the tournament chained below it
      p.delete("tournamentId");
      p.delete("page");
      return p;
    });
  };

  const handleTournamentIdChange = (value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set("tournamentId", value);
      else p.delete("tournamentId");
      p.delete("page");
      return p;
    });
  };

  const handlePurposeChange = (value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set("purpose", value);
      else p.delete("purpose");
      // Tournament filter only applies to registrations — clear it when
      // narrowing to reservations or top-ups to avoid an empty result.
      if (value === "Reservation" || value === "TopUp") {
        p.delete("tournamentId");
        p.delete("tournamentDate");
      }
      p.delete("page");
      return p;
    });
  };

  const clearTournamentDateFilter = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("tournamentDate");
      p.delete("tournamentId");
      p.delete("page");
      return p;
    });
  };

  const currentFilter: PaymentStatusOption = searchParams.has("paymentStatus")
    ? ((searchParams.get("paymentStatus") || "") as PaymentStatusOption)
    : "Pending";

  const columns = [
    {
      key: "playerName" as keyof InstaPayRegistrationDTO,
      label: "Player / Team",
      sortable: true,
      render: (v: any, row: InstaPayRegistrationDTO) => {
        if (isTopUpRow(row)) {
          return (
            <div className="txn-player-cell">
              <span className="txn-player-name">{row.playerName || "-"}</span>
            </div>
          );
        }
        if (row.type === "Reservation") {
          return (
            <div className="txn-player-cell">
              <span className="txn-player-name">{row.playerName || row.guestName || "-"}</span>
              {row.guestEmail && (
                <span className="txn-cell-muted txn-player-team">{row.guestEmail}</span>
              )}
            </div>
          );
        }
        const isTeamPayment = row.payForTeam && row.teamMemberCount > 1;
        return (
          <div className="txn-player-cell">
            <span className="txn-player-name">{v || row.teamName}</span>
            {v && (
              <span className="txn-cell-muted txn-player-team">
                {row.teamName}
                {isTeamPayment && (
                  <Tag tone="neutral" className="txn-inline-tag">
                    Paid for {row.teamMemberCount}
                  </Tag>
                )}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "type" as keyof InstaPayRegistrationDTO,
      label: "Purpose",
      sortable: true,
      render: (v: any) => (
        <Tag tone="brand">
          {v === "Reservation" ? "Court Reservation" : v === "TopUp" ? "Balance Top-Up" : "Registration"}
        </Tag>
      ),
    },
    {
      key: "tournamentName" as keyof InstaPayRegistrationDTO,
      label: "Tournament / Court",
      render: (v: any, row: InstaPayRegistrationDTO) => {
        if (isTopUpRow(row)) {
          return <span className="txn-cell-muted">-</span>;
        }
        if (row.type === "Reservation") {
          let timeLabel: string | null = null;
          if (row.reservationStartTime && row.reservationEndTime) {
            timeLabel = isSameCairoDay(row.reservationStartTime, row.reservationEndTime)
              ? `${fmtCairoDate(row.reservationStartTime)} · ${fmtCairoTime(row.reservationStartTime)} – ${fmtCairoTime(row.reservationEndTime)}`
              : `${fmtCairoDateTime(row.reservationStartTime)} – ${fmtCairoDateTime(row.reservationEndTime)}`;
          } else if (row.reservationStartTime) {
            timeLabel = fmtCairoDateTime(row.reservationStartTime);
          }
          return (
            <div className="txn-tournament-cell">
              <span>{row.courtName || "-"}</span>
              {timeLabel && <span className="txn-cell-muted">{timeLabel}</span>}
            </div>
          );
        }
        let dateLabel: string | null = null;
        if (row.tournamentStartDate && row.tournamentEndDate) {
          dateLabel = isSameCairoDay(row.tournamentStartDate, row.tournamentEndDate)
            ? fmtCairoDate(row.tournamentStartDate)
            : `${fmtCairoDate(row.tournamentStartDate)} – ${fmtCairoDate(row.tournamentEndDate)}`;
        } else if (row.tournamentStartDate) {
          dateLabel = fmtCairoDate(row.tournamentStartDate);
        }
        const metaParts = [dateLabel, row.categoryName].filter(Boolean);
        return (
          <div className="txn-tournament-cell">
            <span>{v || "-"}</span>
            {metaParts.length > 0 && (
              <span className="txn-cell-muted">{metaParts.join(" · ")}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "amount" as keyof InstaPayRegistrationDTO,
      label: "Amount",
      render: (v: any, row: InstaPayRegistrationDTO) => (
        <span className="txn-cell-amount positive">
          {formatMoney(v, row.currency)}
        </span>
      ),
    },
    {
      key: "receiptScreenshot" as keyof InstaPayRegistrationDTO,
      label: "Receipt",
      render: (v: any) => v
        ? (
          <button
            className="txn-thumb"
            onClick={(e) => { e.stopPropagation(); setPreviewUrl(v); }}
            aria-label="Preview receipt"
          >
            <img src={v} alt="Receipt thumbnail" />
            <span className="txn-thumb-overlay"><ImageIcon size={14} /></span>
          </button>
        )
        : <span className="txn-cell-muted">No receipt</span>,
    },
    {
      key: "paymentStatus" as keyof InstaPayRegistrationDTO,
      label: "Payment",
      render: (v: any) => <StatusBadge status={v} />,
      sortable: true,
    },
    {
      key: "registrationStatus" as keyof InstaPayRegistrationDTO,
      label: "Registration",
      render: (v: any, row: InstaPayRegistrationDTO) =>
        isTopUpRow(row) ? <span className="txn-cell-muted">-</span> : <StatusBadge status={v} />,
      sortable: true,
    },
    {
      key: "createdAt" as keyof InstaPayRegistrationDTO,
      label: "Submitted",
      render: (v: any) => <span className="txn-cell-date">{fmtCairoDateTime(v)}</span>,
      sortable: true,
    },
    {
      key: "teamMemberId" as keyof InstaPayRegistrationDTO,
      label: "",
      stickyRight: true,
      center: true,
      render: (_: any, row: InstaPayRegistrationDTO) => {
        if (isTopUpRow(row)) {
          // For top-up rows, teamMemberId holds the BalanceTransaction id.
          const txRow = topUps.find((t) => t.id === row.teamMemberId);
          if (!txRow) return null;
          return (
            <div className="txn-row-actions">
              <Button
                variant="ghost"
                size="sm"
                icon={<X size={14} />}
                disabled={topUpBusyId === txRow.id}
                onClick={(e) => { e.stopPropagation(); setTopUpAction({ row: txRow, approve: false }); }}
              >
                Reject
              </Button>
              <Button
                size="sm"
                icon={<Check size={14} />}
                disabled={topUpBusyId === txRow.id}
                onClick={(e) => { e.stopPropagation(); setTopUpAction({ row: txRow, approve: true }); }}
              >
                Approve
              </Button>
            </div>
          );
        }
        if (row.paymentStatus === "Paid") {
          return <Tag tone="ok" icon={<Check size={12} />}>Confirmed</Tag>;
        }
        if (row.paymentStatus === "Rejected") {
          return <Tag tone="danger" icon={<X size={12} />}>Rejected</Tag>;
        }
        // Registrations only: Confirm + Reject. Reservations only get Confirm.
        const isRegistration = row.type !== "Reservation";
        return (
          <div className="txn-row-actions">
            {isRegistration && (
              <Button
                variant="danger-tint"
                size="sm"
                icon={<X size={14} />}
                disabled={rejectingId === rowKey(row) || confirmingId === rowKey(row)}
                onClick={(e) => { e.stopPropagation(); setPendingRejectRow(row); }}
              >
                Reject
              </Button>
            )}
            <Button
              size="sm"
              icon={<Check size={14} />}
              disabled={confirmingId === rowKey(row) || rejectingId === rowKey(row)}
              onClick={(e) => { e.stopPropagation(); setPendingRow(row); }}
            >
              Confirm
            </Button>
          </div>
        );
      },
    },
  ];

  const filterOptions: { value: PaymentStatusOption; label: string }[] = [
    { value: "Pending", label: "Pending" },
    { value: "Paid", label: "Paid" },
    { value: "Rejected", label: "Rejected" },
    { value: "", label: "All" },
  ];

  // Top-ups are always Pending — exclude them when the user filters to "Paid".
  const includeTopUps = showTopUps && paymentStatusFilter !== "Paid";
  const topUpRows = includeTopUps ? topUps.map(topUpToRow) : [];

  // When filtering to TopUp only, the table is just top-ups. Otherwise prepend
  // pending top-ups to the (paginated) registrations.
  const mergedRows: InstaPayRegistrationDTO[] = isTopUpView
    ? topUpRows
    : [...topUpRows, ...t.data];

  return (
    <div className="txn-instapay-tab">
      <Toolbar className="txn-filter-bar">
        {!isTopUpView && (
          <FilterGroup label="Status">
            <Segmented
              options={filterOptions}
              value={currentFilter}
              onChange={handlePaymentStatusChange}
              ariaLabel="Payment status"
            />
          </FilterGroup>
        )}

        <FilterGroup label="Purpose">
          <Select
            className="txn-filter-select"
            aria-label="Purpose"
            value={purposeFilter}
            onChange={(e) => handlePurposeChange(e.target.value)}
          >
            <option value="">All purposes</option>
            <option value="Registration">Registration</option>
            <option value="Reservation">Court Reservation</option>
            <option value="TopUp">Balance Top-Up</option>
          </Select>
        </FilterGroup>

        {purposeFilter !== "Reservation" && purposeFilter !== "TopUp" && (
          <FilterGroup label="Tournament" className="txn-filter--tournament">
            <DateFilterInput value={tournamentDateFilter} onChange={handleTournamentDateChange} />
            {tournamentDateFilter && (
              <Select
                className="txn-filter-select txn-filter-select--wide"
                aria-label="Tournament"
                value={tournamentIdFilter}
                onChange={(e) => handleTournamentIdChange(e.target.value)}
                disabled={dateTournamentsLoading || dateTournaments.length === 0}
              >
                <option value="">
                  {dateTournamentsLoading
                    ? "Loading tournaments..."
                    : dateTournaments.length === 0
                    ? "No tournaments on this date"
                    : "All tournaments"}
                </option>
                {dateTournaments.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            )}
            {(tournamentDateFilter || tournamentIdFilter) && (
              <Button
                variant="ghost"
                icon={<X size={14} />}
                onClick={clearTournamentDateFilter}
                aria-label="Clear tournament filter"
              >
                Clear
              </Button>
            )}
          </FilterGroup>
        )}
      </Toolbar>

      <div className="txn-summary-row">
        {isTopUpView ? (
          <TotalAmountPill
            amount={topUps.reduce((s, r) => s + Number(r.amount || 0), 0)}
            count={topUps.length}
            label="pending top-up"
          />
        ) : (
          <TotalAmountPill amount={t.totalAmount} count={t.totalItems} label="payment" currency={t.data?.[0]?.currency} />
        )}
        {!isTopUpView && (
        <ExportButton
          disabled={t.loading || t.totalItems === 0}
          onExport={async () => {
            const { data } = await getAllInstaPayRegistrations(
              1,
              Math.max(t.totalItems, 1),
              t.searchValue,
              paymentStatusFilter,
              tournamentIdFilter || undefined,
              tournamentDateFilter || undefined,
              t.sortColumn,
              t.sortDirection,
              purposeFilter || undefined,
            );
            downloadCsv<InstaPayRegistrationDTO>(
              data.items,
              [
                { header: "Purpose", value: (r) => r.type === "Reservation" ? "Court Reservation" : "Registration" },
                { header: "Player / Team", value: (r) => r.type === "Reservation" ? (r.playerName || r.guestName || "") : (r.playerName || r.teamName || "") },
                { header: "Team Name", value: (r) => r.teamName || "" },
                { header: "Tournament / Court", value: (r) => r.type === "Reservation" ? (r.courtName || "") : (r.tournamentName || "") },
                { header: "Category", value: (r) => r.categoryName || "" },
                { header: "Reservation Start (Cairo)", value: (r) => fmtCairoDateTime(r.reservationStartTime) === "-" ? "" : fmtCairoDateTime(r.reservationStartTime) },
                { header: "Reservation End (Cairo)", value: (r) => fmtCairoDateTime(r.reservationEndTime) === "-" ? "" : fmtCairoDateTime(r.reservationEndTime) },
                { header: "Pay For Team", value: (r) => r.payForTeam ? "Yes" : "No" },
                { header: "Team Member Count", value: (r) => r.teamMemberCount ?? "" },
                { header: "Amount", value: (r) => Number(r.amount).toFixed(2) },
                { header: "Currency", value: (r) => (r.currency ?? "").trim() || "EGP" },
                { header: "Payment Status", value: (r) => r.paymentStatus },
                { header: "Registration Status", value: (r) => r.registrationStatus },
                { header: "Submitted (Cairo)", value: (r) => fmtCairoDateTime(r.createdAt) },
                { header: "Receipt URL", value: (r) => r.receiptScreenshot || "" },
              ],
              `instapay-payments-${todayStamp()}.csv`,
            );
          }}
        />
        )}
      </div>

      <DataTable
        id="instapay-registrations"
        data={mergedRows}
        label="InstaPay payments"
        columns={columns}
        pageNumber={isTopUpView ? 1 : t.pageNumber}
        pageSize={isTopUpView ? Math.max(topUps.length, 1) : t.pageSize}
        totalPages={isTopUpView ? 1 : t.totalPages}
        totalItems={isTopUpView ? topUps.length : t.totalItems + topUps.length}
        loading={isTopUpView ? topUpsLoading : t.loading || (showTopUps && topUpsLoading)}
        searchValue={t.searchValue}
        onSearchChange={t.setSearchValue}
        onSearch={() => {}}
        onViewItem={() => {}}
        onPageChange={t.handlePageChange}
        onSortChange={t.handleSortChange}
        sortColumn={t.sortColumn}
        sortDirection={t.sortDirection}
      />

      {shownPendingRow && (
        <ConfirmPaymentDialog
          open={!!pendingRow}
          row={shownPendingRow}
          busy={confirmingId === rowKey(shownPendingRow)}
          onCancel={() => setPendingRow(null)}
          onConfirm={handleConfirm}
        />
      )}
      {shownRejectRow && (
        <RejectPaymentDialog
          open={!!pendingRejectRow}
          row={shownRejectRow}
          busy={rejectingId === rowKey(shownRejectRow)}
          onCancel={() => setPendingRejectRow(null)}
          onReject={handleReject}
        />
      )}
      {shownTopUpAction && (
        <TopUpReviewDialog
          open={!!topUpAction}
          action={shownTopUpAction}
          busy={topUpBusyId === shownTopUpAction.row.id}
          onCancel={() => setTopUpAction(null)}
          onConfirm={handleReviewTopUp}
        />
      )}
      {previewUrl && <ImageLightbox src={previewUrl} onClose={() => setPreviewUrl(null)} />}
    </div>
  );
};

// ── Paginated hook ──
function usePaginatedTab<T>(
  fetchFn: React.MutableRefObject<(page: number, size: number, search: string, sort: string, dir: string) => Promise<{ data: { items: T[]; pagination: { total: number; totalPages: number }; totalAmount?: number } }>>,
  searchParams: URLSearchParams,
  setSearchParams: ReturnType<typeof useSearchParams>[1]
) {
  const pageSize = 15;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const fetchIdRef = useRef(0);

  // URL is the single source of truth — fetch only runs when the URL changes.
  // This avoids any race between local state and URL (which was firing two fetches
  // per click: one for the new page, one from a reset effect).
  const pageNumber = parseInt(searchParams.get("page") || "1");
  const sortColumn = searchParams.get("sortColumn") || "createdAt";
  const sortDirection = (searchParams.get("sortDirection") || "desc") as "asc" | "desc";
  const search = searchParams.get("search") || "";
  const paramsKey = searchParams.toString();

  const searchCallback = useCallback((val: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev.toString());
      if (val) p.set("search", val); else p.delete("search");
      p.delete("page");
      return p;
    });
  }, [setSearchParams]);
  const { searchValue, setSearchValue } = useDebouncedSearch(searchCallback, 500, search);

  useEffect(() => {
    const id = ++fetchIdRef.current;
    setLoading(true);
    fetchFn.current(pageNumber, pageSize, search, sortColumn, sortDirection)
      .then(({ data: result }) => {
        if (fetchIdRef.current !== id) return;
        setData(result.items);
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.total);
        setTotalAmount(result.totalAmount);
      })
      .catch((err) => {
        console.error("[usePaginatedTab] fetch failed", err);
      })
      .finally(() => { if (fetchIdRef.current === id) setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const handlePageChange = useCallback((newPage: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev.toString());
      if (newPage > 1) p.set("page", newPage.toString()); else p.delete("page");
      return p;
    });
  }, [setSearchParams]);

  const handleSortChange = useCallback((col: string, dir: "asc" | "desc") => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev.toString());
      p.set("sortColumn", col); p.set("sortDirection", dir); p.delete("page");
      return p;
    });
  }, [setSearchParams]);

  return { data, loading, pageNumber, pageSize, totalPages, totalItems, totalAmount, sortColumn, sortDirection, searchValue, setSearchValue, handlePageChange, handleSortChange };
}

const POINTS_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "Earn", label: "Earn" },
  { value: "Conversion", label: "Redeemed" },
  { value: "Expiry", label: "Expiry" },
  { value: "Clawback", label: "Clawback" },
  { value: "AdminCredit", label: "Admin Credit" },
  { value: "AdminDebit", label: "Admin Debit" },
];
const pointsTypeLabel = (type: string) =>
  POINTS_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

const PointsTotalPill = ({ amount, count }: { amount: number | undefined; count: number }) => {
  if (amount === undefined) return null;
  return (
    <div className="txn-total-amount">
      <span className="txn-total-amount-label">{count.toLocaleString()} {count === 1 ? "entry" : "entries"} · Total</span>
      <span className="txn-total-amount-value">{Number(amount).toLocaleString()} pts</span>
    </div>
  );
};

// ── All Balance Transactions ──
const BALANCE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "InstaPay", label: "InstaPay Top-Up" },
  { value: "FawryTopUp", label: "Fawry Top-Up" },
  { value: "TopUpBonus", label: "Top-Up Bonus" },
  { value: "TopUpBonusReversal", label: "Top-Up Bonus Reversal" },
  { value: "FawryRefund", label: "Fawry Refund" },
  { value: "TournamentPayment", label: "Tournament Payment" },
  { value: "ReservationPayment", label: "Reservation Payment" },
  { value: "PointsConversion", label: "Clash Points Redeemed" },
  { value: "AdminCredit", label: "Admin Credit" },
  { value: "AdminDebit", label: "Admin Debit" },
  { value: "TransferIn", label: "Transfer In" },
  { value: "TransferOut", label: "Transfer Out" },
];
const BALANCE_STATUS_OPTIONS = ["Pending", "Approved", "Rejected"];

const AllTransactionsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";
  const typeFilter = searchParams.get("type") || "";

  const fetchRef = useRef((page: number, size: number, search: string, sort: string, dir: string) =>
    getAllBalanceTransactions(page, size, search, statusFilter, typeFilter, sort, dir));
  fetchRef.current = (page, size, search, sort, dir) =>
    getAllBalanceTransactions(page, size, search, statusFilter, typeFilter, sort, dir);

  const t = usePaginatedTab<BalanceTransactionDTO>(fetchRef, searchParams, setSearchParams);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, value); else p.delete(key);
      p.delete("page");
      return p;
    });
  };

  const hasAnyFilter = !!(statusFilter || typeFilter);

  const columns = [
    { key: "userName" as keyof BalanceTransactionDTO, label: "User" },
    { key: "type" as keyof BalanceTransactionDTO, label: "Type", render: (v: any, row: BalanceTransactionDTO) => <LedgerTypeTag amount={row.amount}>{String(v).replace(/([A-Z])/g, " $1").trim()}</LedgerTypeTag>, sortable: true },
    { key: "amount" as keyof BalanceTransactionDTO, label: "Amount", render: (v: any) => <span className={`txn-cell-amount ${Number(v) > 0 ? "positive" : "negative"}`}>{Number(v) > 0 ? "+" : ""}{Number(v).toFixed(2)} EGP</span>, sortable: true },
    { key: "balanceAfter" as keyof BalanceTransactionDTO, label: "Balance After", render: (v: any) => <span className="txn-cell-balance">{Number(v).toFixed(2)} EGP</span> },
    { key: "status" as keyof BalanceTransactionDTO, label: "Status", render: (v: any) => <StatusBadge status={v} />, sortable: true },
    { key: "description" as keyof BalanceTransactionDTO, label: "Description", render: (v: any, row: BalanceTransactionDTO) => {
      const isAdminAdjust = row.type === "AdminCredit" || row.type === "AdminDebit";
      return (
        <span className="txn-cell-desc">
          {v || "-"}
          {isAdminAdjust && row.createdByName && (
            <span className="txn-cell-muted"> (by {row.createdByName})</span>
          )}
        </span>
      );
    } },
    { key: "createdAt" as keyof BalanceTransactionDTO, label: "Date", render: (v: any) => <span className="txn-cell-date">{fmtCairoDateTime(v)}</span>, sortable: true },
  ];

  return (
    <div className="txn-instapay-tab">
      <Toolbar
        className="txn-filter-bar"
        actions={
          hasAnyFilter && (
            <Button
              variant="ghost"
              icon={<X size={14} />}
              onClick={() => {
                setSearchParams((prev) => {
                  const p = new URLSearchParams(prev);
                  p.delete("type");
                  p.delete("status");
                  p.delete("page");
                  return p;
                });
              }}
            >
              Reset all
            </Button>
          )
        }
      >
        <FilterGroup label="Type">
          <Select
            className="txn-filter-select"
            aria-label="Type"
            value={typeFilter}
            onChange={(e) => setParam("type", e.target.value)}
          >
            <option value="">All types</option>
            {BALANCE_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FilterGroup>

        <FilterGroup label="Status">
          <Select
            className="txn-filter-select"
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => setParam("status", e.target.value)}
          >
            <option value="">All statuses</option>
            {BALANCE_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FilterGroup>
      </Toolbar>

      <div className="txn-summary-row">
        <ExportButton
          disabled={t.loading || t.totalItems === 0}
          onExport={async () => {
            const { data } = await getAllBalanceTransactions(
              1,
              Math.max(t.totalItems, 1),
              t.searchValue,
              statusFilter,
              typeFilter,
              t.sortColumn,
              t.sortDirection,
            );
            downloadCsv<BalanceTransactionDTO>(
              data.items,
              [
                { header: "User", value: (r) => r.userName || "" },
                { header: "Type", value: (r) => String(r.type).replace(/([A-Z])/g, " $1").trim() },
                { header: "Amount (EGP)", value: (r) => Number(r.amount).toFixed(2) },
                { header: "Balance After (EGP)", value: (r) => Number(r.balanceAfter).toFixed(2) },
                { header: "Status", value: (r) => r.status },
                { header: "Description", value: (r) => r.description || "" },
                { header: "Created By", value: (r) => r.createdByName || "" },
                { header: "Date (Cairo)", value: (r) => fmtCairoDateTime(r.createdAt) },
              ],
              `balance-transactions-${todayStamp()}.csv`,
            );
          }}
        />
      </div>
      <DataTable id="all-transactions" data={t.data} label="balance transactions" columns={columns} pageNumber={t.pageNumber} pageSize={t.pageSize} totalPages={t.totalPages} totalItems={t.totalItems}
        loading={t.loading} searchValue={t.searchValue} onSearchChange={t.setSearchValue} onSearch={() => {}} onViewItem={() => {}} onPageChange={t.handlePageChange}
        onSortChange={t.handleSortChange} sortColumn={t.sortColumn} sortDirection={t.sortDirection} />
    </div>
  );
};

// ── Online Payments (all Fawry transactions: top-ups, registrations, court reservations) ──
const FAWRY_STATUSES = ["Initiated", "Paid", "Failed", "Expired", "Cancelled", "Refunded"] as const;
const FAWRY_METHODS = ["CARD", "PayAtFawry", "MWALLET", "VALU", "APPLE_PAY", "MEEZA_DIGITAL"];

const PURPOSE_LABELS: Record<string, string> = {
  BalanceTopUp: "Top-Up",
  Registration: "Registration",
  CourtReservation: "Court Reservation",
};

const PaymentsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get("fawryStatus") || "";
  // Default to `any` — only transactions with a payment method, so totals on
  // this page match the Fawry merchant dashboard. The user can pick "All"
  // (sentinel `all` in the URL) or a specific method.
  const rawMethodParam = searchParams.get("fawryMethod");
  const methodFilter = rawMethodParam ?? "any";
  const purposeFilter = searchParams.get("purpose") || "";
  const tournamentDateFilter = searchParams.get("tournamentDate") || "";
  const tournamentIdFilter = searchParams.get("tournamentId") || "";
  const [dateTournaments, setDateTournaments] = useState<InstaPayTournamentOptionDTO[]>([]);
  const [dateTournamentsLoading, setDateTournamentsLoading] = useState(false);

  useEffect(() => {
    if (!tournamentDateFilter) {
      setDateTournaments([]);
      return;
    }
    setDateTournamentsLoading(true);
    getFawryTournaments(tournamentDateFilter)
      .then(({ data }) => setDateTournaments(data || []))
      .catch(() => setDateTournaments([]))
      .finally(() => setDateTournamentsLoading(false));
  }, [tournamentDateFilter]);

  useEffect(() => {
    if (!tournamentIdFilter || !tournamentDateFilter) return;
    if (dateTournamentsLoading) return;
    if (!dateTournaments.some((tour) => tour.id === tournamentIdFilter)) {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete("tournamentId");
        p.delete("page");
        return p;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateTournaments, dateTournamentsLoading]);

  // `all` in the URL means "no method filter"; everything else (including
  // `any`) is forwarded to the backend as-is.
  const apiMethodFilter = methodFilter === "all" ? "" : methodFilter;

  const fetchRef = useRef((page: number, size: number, search: string, sort: string, dir: string) =>
    getAllFawryTransactions(page, size, search, statusFilter, purposeFilter, apiMethodFilter, tournamentIdFilter || undefined, tournamentDateFilter || undefined, sort, dir));
  fetchRef.current = (page, size, search, sort, dir) =>
    getAllFawryTransactions(page, size, search, statusFilter, purposeFilter, apiMethodFilter, tournamentIdFilter || undefined, tournamentDateFilter || undefined, sort, dir);

  const t = usePaginatedTab<FawryTransactionDTO>(fetchRef, searchParams, setSearchParams);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, value); else p.delete(key);
      p.delete("page");
      return p;
    });
  };

  const handleTournamentDateChange = (value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set("tournamentDate", value);
      else p.delete("tournamentDate");
      p.delete("tournamentId");
      p.delete("page");
      return p;
    });
  };

  const handleTournamentIdChange = (value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set("tournamentId", value);
      else p.delete("tournamentId");
      p.delete("page");
      return p;
    });
  };

  const clearAllFilters = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("fawryStatus");
      p.delete("fawryMethod");
      p.delete("purpose");
      p.delete("tournamentDate");
      p.delete("tournamentId");
      p.delete("page");
      return p;
    });
  };

  // `any` is the default method — don't surface it as an active filter.
  const hasAnyFilter = !!(statusFilter || (methodFilter && methodFilter !== "any") || purposeFilter || tournamentDateFilter || tournamentIdFilter);

  const renderSubject = (row: FawryTransactionDTO) => {
    if (row.purpose === "CourtReservation") {
      let timeLabel: string | null = null;
      if (row.reservationStartTime && row.reservationEndTime) {
        timeLabel = isSameCairoDay(row.reservationStartTime, row.reservationEndTime)
          ? `${fmtCairoDate(row.reservationStartTime)} · ${fmtCairoTime(row.reservationStartTime)} – ${fmtCairoTime(row.reservationEndTime)}`
          : `${fmtCairoDateTime(row.reservationStartTime)} – ${fmtCairoDateTime(row.reservationEndTime)}`;
      } else if (row.reservationStartTime) {
        timeLabel = fmtCairoDateTime(row.reservationStartTime);
      }
      return (
        <div className="txn-tournament-cell">
          <span>{row.courtName || row.userName || "-"}</span>
          {timeLabel && <span className="txn-cell-muted">{timeLabel}</span>}
        </div>
      );
    }
    if (row.purpose === "Registration") {
      let dateLabel: string | null = null;
      if (row.tournamentStartDate && row.tournamentEndDate) {
        dateLabel = isSameCairoDay(row.tournamentStartDate, row.tournamentEndDate)
          ? fmtCairoDate(row.tournamentStartDate)
          : `${fmtCairoDate(row.tournamentStartDate)} – ${fmtCairoDate(row.tournamentEndDate)}`;
      } else if (row.tournamentStartDate) {
        dateLabel = fmtCairoDate(row.tournamentStartDate);
      }
      const metaParts = [dateLabel, row.categoryName].filter(Boolean);
      return (
        <div className="txn-tournament-cell">
          <span>{row.tournamentName || row.teamName || "-"}</span>
          {metaParts.length > 0 && (
            <span className="txn-cell-muted">{metaParts.join(" · ")}</span>
          )}
        </div>
      );
    }
    // BalanceTopUp
    return (
      <div className="txn-tournament-cell">
        <span className="txn-cell-muted">Wallet top-up</span>
      </div>
    );
  };

  const columns = [
    {
      key: "userName" as keyof FawryTransactionDTO,
      label: "User",
      sortable: false,
      render: (_v: any, row: FawryTransactionDTO) => (
        <div className="txn-player-cell">
          <span className="txn-player-name">{row.userName || "-"}</span>
          {row.purpose === "Registration" && row.teamName && (
            <span className="txn-cell-muted txn-player-team">
              {row.teamName}
              {row.payForTeam && (
                <Tag tone="neutral" className="txn-inline-tag">Entire Team</Tag>
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "purpose" as keyof FawryTransactionDTO,
      label: "Purpose",
      sortable: true,
      render: (v: any) => <Tag tone="brand">{PURPOSE_LABELS[v] || v}</Tag>,
    },
    {
      key: "tournamentName" as keyof FawryTransactionDTO,
      label: "Details",
      render: (_v: any, row: FawryTransactionDTO) => renderSubject(row),
    },
    { key: "amount" as keyof FawryTransactionDTO, label: "Amount", render: (v: any) => <span className="txn-cell-amount positive">{Number(v).toFixed(2)} EGP</span>, sortable: true },
    { key: "status" as keyof FawryTransactionDTO, label: "Status", render: (v: any) => <StatusBadge status={v} />, sortable: true },
    { key: "paymentMethod" as keyof FawryTransactionDTO, label: "Method", render: (v: any) => <span>{v || "-"}</span> },
    { key: "merchantRefNumber" as keyof FawryTransactionDTO, label: "Merchant Ref", render: (v: any) => <span className="txn-cell-mono">{v}</span> },
    { key: "createdAt" as keyof FawryTransactionDTO, label: "Date", render: (v: any) => <span className="txn-cell-date">{fmtCairoDateTime(v)}</span>, sortable: true },
  ];

  return (
    <div className="txn-instapay-tab">
      <Toolbar
        className="txn-filter-bar"
        actions={
          hasAnyFilter && (
            <Button variant="ghost" icon={<X size={14} />} onClick={clearAllFilters}>
              Reset all
            </Button>
          )
        }
      >
        <FilterGroup label="Purpose">
          <Select
            className="txn-filter-select"
            aria-label="Purpose"
            value={purposeFilter}
            onChange={(e) => setParam("purpose", e.target.value)}
          >
            <option value="">All purposes</option>
            <option value="Registration">Registration</option>
            <option value="CourtReservation">Court Reservation</option>
            <option value="BalanceTopUp">Top-Up</option>
          </Select>
        </FilterGroup>

        <FilterGroup label="Status">
          <Select
            className="txn-filter-select"
            aria-label="Status"
            value={statusFilter}
            onChange={(e) => setParam("fawryStatus", e.target.value)}
          >
            <option value="">All statuses</option>
            {FAWRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FilterGroup>

        <FilterGroup label="Method">
          <Select
            className="txn-filter-select"
            aria-label="Method"
            value={methodFilter}
            onChange={(e) => {
              const value = e.target.value;
              setSearchParams((prev) => {
                const p = new URLSearchParams(prev);
                // `any` is the default — keep URL clean by omitting it.
                if (value === "any") p.delete("fawryMethod");
                else p.set("fawryMethod", value);
                p.delete("page");
                return p;
              });
            }}
          >
            <option value="any">Any online method</option>
            <option value="all">All (incl. without method)</option>
            {FAWRY_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </FilterGroup>

        <FilterGroup label="Tournament" className="txn-filter--tournament">
          <DateFilterInput value={tournamentDateFilter} onChange={handleTournamentDateChange} />
          {tournamentDateFilter && (
            <Select
              className="txn-filter-select txn-filter-select--wide"
              aria-label="Tournament"
              value={tournamentIdFilter}
              onChange={(e) => handleTournamentIdChange(e.target.value)}
              disabled={dateTournamentsLoading || dateTournaments.length === 0}
            >
              <option value="">
                {dateTournamentsLoading
                  ? "Loading tournaments..."
                  : dateTournaments.length === 0
                  ? "No tournaments on this date"
                  : "All tournaments"}
              </option>
              {dateTournaments.map((tour) => (
                <option key={tour.id} value={tour.id}>{tour.name}</option>
              ))}
            </Select>
          )}
        </FilterGroup>
      </Toolbar>

      <div className="txn-summary-row">
        <TotalAmountPill amount={t.totalAmount} count={t.totalItems} label="payment" />
        <ExportButton
          disabled={t.loading || t.totalItems === 0}
          onExport={async () => {
            const { data } = await getAllFawryTransactions(
              1,
              Math.max(t.totalItems, 1),
              t.searchValue,
              statusFilter,
              purposeFilter,
              apiMethodFilter,
              tournamentIdFilter || undefined,
              tournamentDateFilter || undefined,
              t.sortColumn,
              t.sortDirection,
            );
            downloadCsv<FawryTransactionDTO>(
              data.items,
              [
                { header: "Purpose", value: (r) => PURPOSE_LABELS[r.purpose] || r.purpose },
                { header: "User", value: (r) => r.userName || "" },
                { header: "Team", value: (r) => r.teamName || "" },
                { header: "Tournament", value: (r) => r.tournamentName || "" },
                { header: "Category", value: (r) => r.categoryName || "" },
                { header: "Court", value: (r) => r.courtName || "" },
                { header: "Reservation Start (Cairo)", value: (r) => r.reservationStartTime ? fmtCairoDateTime(r.reservationStartTime) : "" },
                { header: "Reservation End (Cairo)", value: (r) => r.reservationEndTime ? fmtCairoDateTime(r.reservationEndTime) : "" },
                { header: "Pay For Team", value: (r) => r.payForTeam == null ? "" : r.payForTeam ? "Yes" : "No" },
                { header: "Amount (EGP)", value: (r) => Number(r.amount).toFixed(2) },
                { header: "Paid Amount (EGP)", value: (r) => r.paidAmount == null ? "" : Number(r.paidAmount).toFixed(2) },
                { header: "Fawry Fees (EGP)", value: (r) => r.fawryFees == null ? "" : Number(r.fawryFees).toFixed(2) },
                { header: "Status", value: (r) => r.status },
                { header: "Method", value: (r) => r.paymentMethod || "" },
                { header: "Merchant Ref", value: (r) => r.merchantRefNumber },
                { header: "Fawry Ref", value: (r) => r.fawryRefNumber || "" },
                { header: "Failure Reason", value: (r) => r.failureReason || "" },
                { header: "Created (Cairo)", value: (r) => fmtCairoDateTime(r.createdAt) },
                { header: "Paid At (Cairo)", value: (r) => r.paidAt ? fmtCairoDateTime(r.paidAt) : "" },
              ],
              `online-payments-${todayStamp()}.csv`,
            );
          }}
        />
      </div>

      <DataTable
        id="online-payments"
        data={t.data}
        label="online payments"
        columns={columns}
        pageNumber={t.pageNumber}
        pageSize={t.pageSize}
        totalPages={t.totalPages}
        totalItems={t.totalItems}
        loading={t.loading}
        searchValue={t.searchValue}
        onSearchChange={t.setSearchValue}
        onSearch={() => {}}
        onViewItem={() => {}}
        onPageChange={t.handlePageChange}
        onSortChange={t.handleSortChange}
        sortColumn={t.sortColumn}
        sortDirection={t.sortDirection}
      />
    </div>
  );
};

// ── Clash Points (all-users points ledger) ──
const ClashPointsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get("type") || "";

  const fetchRef = useRef((page: number, size: number, search: string, sort: string, dir: string) =>
    getAllClashPointsTransactions(page, size, search, typeFilter, sort, dir));
  fetchRef.current = (page, size, search, sort, dir) =>
    getAllClashPointsTransactions(page, size, search, typeFilter, sort, dir);

  const t = usePaginatedTab<AdminClashPointsTransactionDTO>(fetchRef, searchParams, setSearchParams);

  const setParam = (key: string, value: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, value); else p.delete(key);
      p.delete("page");
      return p;
    });
  };

  const columns = [
    { key: "userName" as keyof AdminClashPointsTransactionDTO, label: "User" },
    {
      key: "type" as keyof AdminClashPointsTransactionDTO,
      label: "Type",
      sortable: true,
      render: (v: any, row: AdminClashPointsTransactionDTO) => (
        <LedgerTypeTag amount={row.amount}>{pointsTypeLabel(String(v))}</LedgerTypeTag>
      ),
    },
    {
      key: "amount" as keyof AdminClashPointsTransactionDTO,
      label: "Points",
      sortable: true,
      render: (v: any) => (
        <span className={`txn-cell-amount ${Number(v) > 0 ? "positive" : "negative"}`}>
          {Number(v) > 0 ? "+" : ""}{Number(v).toLocaleString()}
        </span>
      ),
    },
    {
      key: "balanceAfter" as keyof AdminClashPointsTransactionDTO,
      label: "Balance After",
      render: (v: any) => <span className="txn-cell-balance">{Number(v).toLocaleString()}</span>,
    },
    {
      key: "description" as keyof AdminClashPointsTransactionDTO,
      label: "Description",
      render: (v: any) => <span className="txn-cell-desc">{v || "-"}</span>,
    },
    {
      key: "createdAt" as keyof AdminClashPointsTransactionDTO,
      label: "Date",
      sortable: true,
      render: (v: any) => <span className="txn-cell-date">{fmtCairoDateTime(v)}</span>,
    },
  ];

  return (
    <div className="txn-instapay-tab">
      <Toolbar
        className="txn-filter-bar"
        actions={
          typeFilter && (
            <Button variant="ghost" icon={<X size={14} />} onClick={() => setParam("type", "")}>
              Reset all
            </Button>
          )
        }
      >
        <FilterGroup label="Type">
          <Select
            className="txn-filter-select"
            aria-label="Type"
            value={typeFilter}
            onChange={(e) => setParam("type", e.target.value)}
          >
            <option value="">All types</option>
            {POINTS_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </FilterGroup>
      </Toolbar>

      <div className="txn-summary-row">
        <PointsTotalPill amount={t.totalAmount} count={t.totalItems} />
        <ExportButton
          disabled={t.loading || t.totalItems === 0}
          onExport={async () => {
            const { data } = await getAllClashPointsTransactions(
              1,
              Math.max(t.totalItems, 1),
              t.searchValue,
              typeFilter,
              t.sortColumn,
              t.sortDirection,
            );
            downloadCsv<AdminClashPointsTransactionDTO>(
              data.items,
              [
                { header: "User", value: (r) => r.userName || "" },
                { header: "Type", value: (r) => pointsTypeLabel(r.type) },
                { header: "Points", value: (r) => r.amount },
                { header: "Balance After", value: (r) => r.balanceAfter },
                { header: "Description", value: (r) => r.description || "" },
                { header: "Date (Cairo)", value: (r) => fmtCairoDateTime(r.createdAt) },
              ],
              `clash-points-transactions-${todayStamp()}.csv`,
            );
          }}
        />
      </div>

      <DataTable
        id="clash-points-transactions"
        data={t.data}
        label="clash points transactions"
        columns={columns}
        pageNumber={t.pageNumber}
        pageSize={t.pageSize}
        totalPages={t.totalPages}
        totalItems={t.totalItems}
        loading={t.loading}
        searchValue={t.searchValue}
        onSearchChange={t.setSearchValue}
        onSearch={() => {}}
        onViewItem={() => {}}
        onPageChange={t.handlePageChange}
        onSortChange={t.handleSortChange}
        sortColumn={t.sortColumn}
        sortDirection={t.sortDirection}
      />
    </div>
  );
};

// ── Main ──
const Transactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("tab") as Tab) || "instapay";

  const setTab = (tab: Tab) => {
    const p = new URLSearchParams();
    p.set("tab", tab);
    setSearchParams(p);
  };

  useEffect(() => { document.title = "Transactions"; }, []);

  const tabs: { key: Tab; label: string }[] = [
    { key: "instapay", label: "InstaPay Payments" },
    { key: "payments", label: "Online Payments" },
    { key: "all", label: "Balance Transactions" },
    { key: "clash-points", label: "Clash Points" },
  ];

  return (
    <div className="transactions-page">
      <PageHeader title="Transactions" subtitle="Manage payments, top-ups, and transaction history" />

      <Tabs
        className="txn-tabs"
        ariaLabel="Transactions"
        options={tabs.map((t) => ({ value: t.key, label: t.label }))}
        value={activeTab}
        onChange={setTab}
      />

      <div className="txn-tab-panel">
        {activeTab === "instapay" && <InstaPayRegistrationsTab />}
        {activeTab === "payments" && <PaymentsTab />}
        {activeTab === "all" && <AllTransactionsTab />}
        {activeTab === "clash-points" && <ClashPointsTab />}
      </div>
    </div>
  );
};

export default Transactions;
